import { ReactNode } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { ClearButton } from '@morressier/alembic';
import '../styles.css';

interface SingleItemProps {
  children: ReactNode;
  width?: string;
  bgColor?: string;
}

interface SingleItemSubComponentProps {
  children: ReactNode;
}

interface ContentItemComponentProps {
  size?: 'normal' | 'large' | 'xl' | 'small' | 'default' | 'fullWidth';
  fullWidth?: boolean;
  bgColor?: string;
}

const SingleItem: React.FC<SingleItemProps> & {
  Sidebar: React.FC<SingleItemProps>;
  Navigation: React.FC<
    SingleItemSubComponentProps & {
      size?: 'compact' | 'normal';
      bgColor?: string;
    }
  >;
  Tab: React.FC<
    SingleItemSubComponentProps & { isActive: boolean; onClick: () => void }
  >;
  Content: React.FC<SingleItemSubComponentProps & ContentItemComponentProps>;
  MainArea: React.FC<SingleItemSubComponentProps>;
  Header: React.FC<
    SingleItemSubComponentProps & {
      bgColor?: string;
      showBorderBottom?: boolean;
    }
  >;
} = ({ children, width = '100%', bgColor = 'surfaceSecondaryFill' }) => {
  return (
    <Flex
      width={width}
      minHeight="100vh"
      backgroundColor={bgColor}
      flexDirection={{ base: 'column', xl: 'row' }}
    >
      {children}
    </Flex>
  );
};

SingleItem.Sidebar = ({
  children,
  width = '600px',
  bgColor = 'surfaceTertiaryFill'
}) => {
  return (
    <Flex
      width={{ base: '100%', xl: width }}
      bgColor={bgColor}
      minHeight="100%"
      flexDirection="column"
    >
      {children}
    </Flex>
  );
};

const NavigationSizeStrategies = {
  compact: {
    paddingX: 0,
    height: 12
  },
  normal: {
    paddingX: 6,
    height: 16
  }
};

SingleItem.Navigation = ({
  children,
  size = 'normal',
  bgColor = 'neutralPrimaryBorder'
}) => {
  const { paddingX, height } =
    NavigationSizeStrategies[size] || NavigationSizeStrategies.normal;

  return (
    <Flex
      paddingX={paddingX}
      height={height}
      alignItems="center"
      borderBottomWidth="1px"
      borderColor={bgColor}
      borderStyle="solid"
      overflowX="auto"
      flexWrap={{ base: 'wrap', md: 'nowrap' }}
    >
      {children}
    </Flex>
  );
};

SingleItem.Tab = ({ children, isActive, onClick }) => {
  return (
    <Flex
      paddingX={4}
      borderBottomWidth="2px"
      borderStyle="solid"
      justifyContent="center"
      alignItems="center"
      height="100%"
      _hover={{ bg: 'actionSecondaryFill' }}
      onClick={onClick}
      className={`tab-flex-container ${isActive ? 'is-active' : ''}`}
    >
      <ClearButton
        height="100%"
        ariaLabel={`${children} tab`}
        onClick={onClick}
        textTransform="capitalize"
      >
        {children}
      </ClearButton>
    </Flex>
  );
};

SingleItem.MainArea = ({ children }) => {
  return <Box>{children}</Box>;
};

SingleItem.Header = ({
  children,
  bgColor = 'surfaceSecondaryFill',
  showBorderBottom = true,
}) => {
  return (
    <Box
      width="100%"
      bgColor={bgColor}
      borderBottomWidth={showBorderBottom ? '1px' : 0}
      borderColor="neutralPrimaryBorder"
      borderStyle="solid"
    >
      {children}
    </Box>
  );
};

const sizeStrategies = {
  small: {
    padding: '32px 0',
    width: '100%',
    minWidth: { base: '100%', md: '768px' },
    maxWidth: 'unset',
    flexDirection: false,
    containerWidth: '100%',
    margin: 'auto'
  },
  default: {
    padding: 6,
    width: undefined,
    minWidth: { base: '100%', xl: '768px' },
    maxWidth: 'unset',
    flexDirection: true,
    containerWidth: { base: '100%', xl: 'calc(100vw - 500px)' },
    margin: 'auto'
  },
  normal: {
    padding: 16,
    width: '768px',
    minWidth: '768px',
    maxWidth: 'unset',
    flexDirection: false,
    containerWidth: 'calc(100vw - 600px)',
    margin: 'auto'
  },
  large: {
    padding: 0,
    width: undefined,
    minWidth: '768px',
    maxWidth: 'unset',
    flexDirection: false,
    containerWidth: 'calc(100vw - 600px)',
    margin: 'auto'
  },
  xl: {
    padding: 8,
    width: '100%',
    minWidth: '960px',
    maxWidth: '1200px',
    flexDirection: false,
    containerWidth: 'calc(100vw - 600px)',
    margin: 'auto'
  },
  fullWidth: {
    padding: 0,
    width: '100vw',
    minWidth: '100vw',
    maxWidth: '100vw',
    flexDirection: false,
    containerWidth: '100vw',
    margin: 0
  }
};

SingleItem.Content = ({
  children,
  size = 'normal',
  bgColor = 'surfaceSecondaryFill'
}) => {
  const {
    padding,
    width,
    minWidth,
    maxWidth,
    containerWidth,
    margin,
    flexDirection
  } = sizeStrategies[size] || sizeStrategies.normal;

  return (
    <Flex
      width={containerWidth}
      minWidth={minWidth}
      bgColor={bgColor}
      justifyContent="center"
      padding={padding}
      margin={margin}
      {...(flexDirection ? { flexDirection: 'column' } : {})}
    >
      <Box width={width} maxWidth={maxWidth}>
        {children}
      </Box>
    </Flex>
  );
};

export default SingleItem;
